import { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";
import { applySearchParams } from "~/components/build/Show/lib/urlForView";
import { Job } from "~/components/build/Show/lib/types";
import { getDefaultJobTab, isTabAvailable } from "~/components/job/Job/jobTabs";

type UseJobTabSyncOptions = {
  job: Job | null | undefined;
};

type UseJobTabSyncReturn = {
  activeTab: string | null | undefined;
  onTabChange: (tabId: string) => void;
};

/**
 * Synchronizes the active job tab between URL parameters and Zustand store.
 *
 * This hook manages the tab state when viewing a job:
 * - If a tab is in the URL and valid for the job, it uses that
 * - If the tab is invalid for the job, it redirects to the default tab
 * - Otherwise, it checks the Zustand store for a previously selected tab
 * - Finally, it falls back to a default tab (annotations if available, otherwise output)
 *
 * The tab is persisted to both the URL (for sharing) and the store (for navigation).
 *
 * Returns the active tab and a handler to change tabs.
 */
export function useJobTabSync({ job }: UseJobTabSyncOptions): UseJobTabSyncReturn {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const params = useQueryParams();
  const setActiveJobTab = useBuildStore((state) => state.setActiveJobTab);
  const activeJobTab = useBuildStore((state) => state.activeJobTab);

  // Navigate to a tab in the URL
  const navigateToTab = useCallback(
    (tabId: string, { replace = false } = {}) => {
      const newParams = {
        jobUuid: params.jobUuid,
        stepUuid: params.stepUuid,
        tab: tabId,
      };
      navigate(applySearchParams(pathname, newParams), { replace });
    },
    [navigate, pathname, params.jobUuid, params.stepUuid],
  );

  useEffect(() => {
    if (!job) {
      return;
    }

    const defaultTab = getDefaultJobTab(job);

    // If we have a tab in URL, validate and sync it to the store
    if (params.tab) {
      // If the tab is invalid for this job, redirect to the default tab
      if (!isTabAvailable(job, params.tab)) {
        setActiveJobTab(defaultTab);
        navigateToTab(defaultTab, { replace: true });
        return;
      }

      setActiveJobTab(params.tab);
      return;
    }

    // Check if store has a tab from previous navigation
    if (activeJobTab) {
      // If the stored tab is invalid for this job, use the default instead
      if (!isTabAvailable(job, activeJobTab)) {
        setActiveJobTab(defaultTab);
        navigateToTab(defaultTab, { replace: true });
        return;
      }

      // Apply stored tab to URL immediately
      navigateToTab(activeJobTab, { replace: true });
      return;
    }

    // Use the default tab
    setActiveJobTab(defaultTab);
    navigateToTab(defaultTab, { replace: true });
  }, [
    job,
    params.tab,
    params.jobUuid,
    params.stepUuid,
    navigateToTab,
    setActiveJobTab,
    activeJobTab,
  ]);

  // Update both URL and store when tab changes
  const handleTabChange = useCallback(
    (tabId: string) => {
      setActiveJobTab(tabId);
      navigateToTab(tabId);
    },
    [navigateToTab, setActiveJobTab],
  );

  return {
    activeTab: activeJobTab,
    onTabChange: handleTabChange,
  };
}
